/**
 * SyncDeveloperNetworksFECommand - Sync developer networks from ECGridOS live data
 * Calls POST /api/v1/developer-plan/sync to refresh ES with live mailbox/KC counts.
 * Follows MediatR pattern (Command + Handler in same file)
 */

import type { ICommand, ICommandHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { DeveloperPlanTenant, DeveloperPlanTenantsResponse } from '@/application/queries/developerPlan/developer-plan.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Command (Request)
// ============================================================================

export class SyncDeveloperNetworksFECommand implements ICommand<DeveloperPlanTenantsResponse> {
  readonly type = 'SyncDeveloperNetworksFECommand' as const;
}

// ============================================================================
// Handler
// ============================================================================

export class SyncDeveloperNetworksFECommandHandler
  implements ICommandHandler<SyncDeveloperNetworksFECommand, DeveloperPlanTenantsResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_command: SyncDeveloperNetworksFECommand): Promise<DeveloperPlanTenantsResponse> {
    try {
      const response = await this.httpClient.post<BaseApiResponse<any>>(
        '/api/v1/developer-plan/sync'
      );

      if (!response.succeeded) {
        throw new Error(response.message || 'Failed to sync developer networks');
      }

      const rawData = response.data ?? [];

      // Transform each tenant item (snake_case -> camelCase)
      const tenants: DeveloperPlanTenant[] = (
        Array.isArray(rawData) ? rawData : []
      ).map((t: any) => toCamelCase(t));

      // Build unique status options for client-side filter
      const uniqueStatuses = [...new Set(tenants.map((t) => t.billingStatus).filter(Boolean))];
      const statusOptions = uniqueStatuses.map((s) => ({
        value: s,
        label: s,
      }));

      return { tenants, statusOptions };
    } catch (error: any) {
      if (error.response?.status === 403 || error.status === 403) {
        throw new Error('Forbidden - insufficient permissions');
      }
      throw error;
    }
  }
}
